import FJConstant from '../../constants/FJConstant';
import FJLocalStore from '../../store/FJLocalStore';
import FJMessageStore from '../../store/FJMessageStore';
import FJUtil from '../../util/FJUtil';
import {
    setSliceCreationListAction,
    setSliceHeaderTabIndexAction,
    setSliceTaskStatusAction,
} from '../slices/FJSliceCommon';
import { WATERMARK_REMOVER_USE_CREDIT } from '../slices/FJSliceWatermarkRemover';
import FJActionCommon from './FJActionCommon';
import FJCompressedImageStore from '../../store/FJCompressedImageStore';
import FJImageStore from '../../store/FJImageStore';
import FJNetworkStore from '../../store/FJNetworkStore';
import FJActionModal from './FJActionModal';
import { UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE } from '../../config/dataConfig/FJGenerateData';

let _curEventData = null;
let _currentId = null;

const _checkIsCurrentTimingId = currentTimingId => {
    return _currentId === currentTimingId;
};

const FJActionWatermarkRemover = {
    /**
     * @description 重置
     * @returns {(function(*))|*}
     */
    resetAction: function () {
        return () => {
            _currentId = null;
        };
    },
    /**
     * @description 编辑页面点击close的处理
     * @returns {(function(): Promise<void>)|*}
     */
    editCloseAction: function () {
        return async () => {
            _currentId = null;
        };
    },
    /**
     * 上传图片
     * @param {File} file
     */
    uploadImageAction: function (file, skipTranscode = false) {
        return async dispatch => {
            const format = file.type;
            const isImage = FJUtil.isImage(file, true, UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE);
            const __imageError = () => {
                FJMessageStore.addMessage(
                    FJLocalStore._('unsupported-format-please-try-again-with-supported-formats'),
                    'error',
                );
            };

            FJUtil.eaLog('ai water remove upload', { formats: format });

            if (!isImage) {
                if (skipTranscode) {
                    FJMessageStore.addMessage(
                        FJLocalStore._('unsupported-format-please-try-again-with-supported-formats'),
                        'error',
                    );
                    return;
                }
                // 使用转码功能
                try {
                    const result = await dispatch(
                        FJActionModal.showCloudTranscodingModalAction({
                            files: [file],
                            mediaType: 'image',
                            outputFormat: 'png',
                            onReupload: newFiles => {
                                if (newFiles && newFiles.length > 0) {
                                    // 重新上传文件
                                    dispatch(this.uploadImageAction(newFiles[0]));
                                }
                            },
                        }),
                    );
                    // 直接使用转码后的文件
                    file = result.files[0];
                } catch (e) {
                    if (e.cause === FJConstant.INVALID) {
                        __imageError();
                    }
                    return;
                }
            }

            const inputImageInfo = await FJCompressedImageStore.compressImageByFile(file);

            if (inputImageInfo) {
                dispatch(this._generateAction(inputImageInfo));
            }
        };
    },

    /**
     * 获取任务状态
     * @param {Object} fillInfo   文件或者图片url
     */
    _generateAction: function (fillInfo = {}, isUpload = true) {
        return async (dispatch, getState) => {
            let isFree = false;
            const currentTimingId = new Date().getTime();
            _currentId = currentTimingId;

            const __errorToInitStatus = () => {
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                dispatch(setSliceCreationListAction([]));
            };

            const __errorAndReLoad = eventId => {
                const creationList = getState().common.creationList;
                const reLoadList = creationList.map(i => ({
                    ...i,
                    isLoading: false,
                    isReload: true,
                    eventId,
                }));
                dispatch(setSliceCreationListAction(reLoadList));
                dispatch(setSliceTaskStatusAction(FJConstant.ERROR));
            };

            const __judgeCanGenerate = async () => {
                // 登录判断
                const userSpaceType = await FJUtil.checkUserSpace({
                    errorCallback: () => {
                        FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                    },
                });
                if (userSpaceType > 0) {
                    dispatch(FJActionCommon.userSpaceChangeAction(userSpaceType));
                    return;
                }

                // 额度判断
                const { creditNumber, freeNumCreation } = getState().common;
                const canCredit = creditNumber >= WATERMARK_REMOVER_USE_CREDIT;
                const useFree = !canCredit && freeNumCreation > 0;

                if (!useFree && !canCredit) {
                    dispatch(FJActionModal.showUpgradeModalAction());
                    return;
                }

                isFree = useFree;
                return true;
            };

            const canGenerate = await __judgeCanGenerate();
            if (!canGenerate) {
                return;
            }
            if (!_checkIsCurrentTimingId(currentTimingId)) {
                return;
            }

            const placeHolderList = FJUtil.createPlaceHolderListNew({
                count: 1,
                width: fillInfo.width,
                height: fillInfo.height,
                type: FJConstant.CREATIONS,
            });

            dispatch(setSliceCreationListAction(placeHolderList));
            dispatch(setSliceTaskStatusAction(FJConstant.START));
            dispatch(setSliceHeaderTabIndexAction(FJConstant.CREATIONS));
            _curEventData = null;

            try {
                const s3Url = isUpload ? await FJImageStore.uploadFilesByUrl([fillInfo.src]) : [fillInfo.src];

                if (!s3Url[0]) {
                    FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                    __errorToInitStatus();
                    return;
                }

                if (!_checkIsCurrentTimingId(currentTimingId)) {
                    return;
                }

                const __request = async () => {
                    return await FJNetworkStore.watermarkRemover({ imageUrl: s3Url[0] });
                };

                const eventId = await FJActionCommon.createRequestTask(__request, dispatch);
                _curEventData = {
                    src: s3Url[0],
                    width: fillInfo.width,
                    height: fillInfo.height,
                };

                if (!_checkIsCurrentTimingId(currentTimingId)) {
                    return;
                }

                if (!eventId) {
                    throw new Error('create task error');
                }

                FJUtil.updateAllCreditInfo();
                const tool = FJUtil.getToolName();
                FJUtil.eaLog('ai tool start generating', { 'credit&free': isFree ? 'free' : 'credit', tool });

                const __callback = data => {
                    if (!_checkIsCurrentTimingId(currentTimingId)) {
                        return;
                    }

                    const { type } = data;

                    if (type === FJConstant.ERROR) {
                        __errorAndReLoad(eventId);
                        FJUtil.eaLog('ai_water_remove_generate_error');
                    }

                    if (type === FJConstant.SUCCESS) {
                        FJUtil.eaLog('ai_water_remove_generate_success');
                        FJUtil.eaLog('ai tool generate success', {
                            tool,
                            'credit&free': isFree ? 'free' : 'credit',
                        });
                    }
                };

                dispatch(
                    FJActionCommon.pollingTaskAction([eventId], {
                        callback: __callback,
                        progressNumber: 1,
                        autoDefaultTaskStatus: !FJUtil.isMobile(),
                        isUseFree: isFree,
                    }),
                );
            } catch (error) {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                __errorToInitStatus();
            }
        };
    },

    /**
     * 重试
     * @param {string} eventId
     */
    onRetryAction: function (creationId) {
        return async (dispatch, getState) => {
            const { creationList } = getState().common;
            const creation = creationList.find(i => i.id === creationId);
            if (!creation) {
                return;
            }

            const { eventId } = creation;

            const __errorAndReLoad = () => {
                const creationList = getState().common.creationList;
                const reLoadList = creationList.map(i => ({
                    ...i,
                    isLoading: false,
                    isReload: true,
                }));
                dispatch(setSliceCreationListAction(reLoadList));
                dispatch(setSliceTaskStatusAction(FJConstant.ERROR));
            };

            const __pollingTaskCallback = data => {
                const { type } = data;
                if (type === FJConstant.ERROR) {
                    __errorAndReLoad();
                }
            };

            try {
                const eventState = await FJUtil.checkTaskStatus(null, eventId);

                switch (eventState) {
                    case 'succeeded':
                    case 'processing':
                        dispatch(
                            FJActionCommon.pollingTaskAction([eventId], {
                                callback: __pollingTaskCallback,
                            }),
                        );
                        break;
                    case 'error':
                    case 'failed':
                        dispatch(this._generateAction(_curEventData, false));
                        break;
                    default:
                        break;
                }
            } catch (error) {
                console.log('error', error);
            }
        };
    },
};

export default FJActionWatermarkRemover;
